import cx from "classnames";
import { graphql, PageProps } from "gatsby";
import { getSrc } from "gatsby-plugin-image";
import React from "react";
import type { Asset, MessagePageQuery } from "../../../graphql-types";
import BackgroundImage from "../../components/BackgroundImage";
import { BenefitImageList } from "../../components/BenefitImageList";
import { Breadcrumb } from "../../components/Breadcrumb";
import { BreadcrumbItem } from "../../components/BreadcrumbItem";
import Certificates from "../../components/Certificates";
import Facts from "../../components/Facts";
import Image from "../../components/GatsbyImage";
import { default as GlobalHead } from "../../components/head";
import HorizontalRule from "../../components/HorizontalRule";
import InternalLink from "../../components/InternalLink";
import JSONLDProductList from "../../components/jsonld/JSONProductList";
import Layout from "../../components/layout";
import { RichTextRenderer } from "../../components/RichTextRenderer";
import SocialButtons from "../../components/SocialButtons";
import { TerritoryWidget } from "../../components/TerritoryWidget";
import VariantGrid, { VariantCard } from "../../components/VariantGrid";
import { VideoPlayer } from "../../components/VideoPlayer";
import { SubjectParentBreadcrumbs } from "../../utilities/SubjectParentBreadcrumbs";

type Props = PageProps<MessagePageQuery>;

const Message = ({ data: { home, message } }: Props) => {
  if (!home || !message) {
    throw new Error("Could not query data");
  }
  const {
    absolutePath,
    certificates,
    pageTitle,
    metaDescription,
    facts,
    territoryWidget,
    benefitImages,
    benefitsDisclaimer,
    hero,
    heroImageLink,
    heroVideo,
    main,
    parentSubject,
    summary,
    summaryBackground,
    title,
    dmpAttributes,
    variants,
    id,
  } = message;
  const messageSummaryBackground =
    summaryBackground || home?.coreMessage?.summaryBackground;

  return (
    <Layout
      {...{
        absolutePath,
        home,
        metaDescription: metaDescription ? metaDescription : summary,
        pageTitle,
        shareImgSrc: getSrc(hero?.gatsbyImageData),
        dmpAttributes,
        pageType: "Message",
        testId: "message",
        id,
      }}
    >
      <Breadcrumb>
        {parentSubject && <SubjectParentBreadcrumbs {...parentSubject} />}
        <BreadcrumbItem isCurrent label={title} />
      </Breadcrumb>
      {heroVideo?.cdnVideo ? (
        <VideoPlayer
          data-cy="hero-video"
          className="w-full"
          poster={heroVideo.cdnVideo.posterPath}
          path={heroVideo.cdnVideo.path}
        />
      ) : (
        <div data-cy="hero-image" className="">
          {heroImageLink?.path ? (
            <InternalLink href={heroImageLink.path}>
              <Image
                image={hero?.gatsbyImageData}
                alt={""}
                className="w-full"
              />
              <span className="sr-only">{heroImageLink.title}</span>
            </InternalLink>
          ) : (
            <Image
              image={hero?.gatsbyImageData}
              alt={hero?.description || ""}
              className="w-full"
            />
          )}
        </div>
      )}
      <h1 data-cy="primary-heading" className="text-center title">
        {title}
      </h1>
      {summary && (
        <div
          data-cy="summary-text"
          className={cx("summary", {
            "summary--primary": summary.length < 90,
            "summary--secondary": summary.length >= 90,
            "summary--without-bg": !messageSummaryBackground,
          })}
        >
          {messageSummaryBackground ? (
            <BackgroundImage
              image={messageSummaryBackground.gatsbyImageData}
              alt={""}
              summary={summary}
              loading="eager"
            />
          ) : (
            <span className="mx-auto text-center summary-background-text">
              {summary}
            </span>
          )}
        </div>
      )}
      {benefitImages && <BenefitImageList images={benefitImages} />}
      {main && <RichTextRenderer {...main} className="max-w-xl mx-auto" />}
      <Facts {...{ facts }} />
      {variants?.length > 0 && (
        <>
          <JSONLDProductList
            productUrls={variants.map((v) => v.absolutePath)}
          />
          <VariantGrid>
            {variants.map((related) => (
              <VariantCard key={related.path} variant={related} />
            ))}
          </VariantGrid>
        </>
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {territoryWidget && <TerritoryWidget {...({ territoryWidget } as any)} />}
      {benefitsDisclaimer && (
        <p className="max-w-xl px-10 mx-auto">
          <small
            dangerouslySetInnerHTML={{
              __html: benefitsDisclaimer.replace(/\n/g, "<br>"),
            }}
          />
        </p>
      )}
      <Certificates {...{ certificates }} />
      {main && (
        <>
          <HorizontalRule className="my-10" />
          <SocialButtons
            absolutePath={absolutePath}
            subtitle={summary}
            img={hero as Asset}
          />
          <HorizontalRule className="message-bottom" />
        </>
      )}
    </Layout>
  );
};

export const query = graphql`
  query MessagePage($messageId: String!) {
    home: website {
      ...homeFields
    }
    message(id: { eq: $messageId }) {
      id
      absolutePath
      summary
      title
      pageTitle
      metaDescription
      main {
        raw
        references {
          description
          gatsbyImageData(width: 1200, layout: CONSTRAINED)
          sourceUrl
          cdnVideo {
            path
            posterPath
          }
          localFile {
            publicURL
          }
        }
      }
      dmpAttributes
      hero {
        description
        gatsbyImageData(width: 1280, layout: CONSTRAINED)
      }
      heroImageLink {
        ... on LandingPage {
          path
          title
        }
        ... on Message {
          path
          title
        }
        ... on SignUpFormWidget {
          path
          title
        }
        ... on Subject {
          path
          title: heading
        }
        ... on Hyperlink {
          path: url
          title
        }
      }
      heroVideo {
        cdnVideo {
          path
          posterPath
        }
      }
      certificates {
        image {
          description
          gatsbyImageData(width: 200, layout: CONSTRAINED)
        }
        title
        url
      }
      summaryBackground {
        description
        gatsbyImageData(width: 1280, layout: CONSTRAINED)
        title
      }
      benefitImages {
        description
        localFile {
          publicURL
        }
      }
      benefitsDisclaimer
      territoryWidget {
        bottomGraphic {
          description
          localFile {
            publicURL
          }
        }
        bottomHeading
        graphicHeading
        topCopy
        topHeading
        topHeroImage {
          description
          cdnVideo {
            path
            posterPath
          }
          localFile {
            childImageSharp {
              gatsbyImageData(width: 1280, layout: CONSTRAINED)
            }
          }
        }
        topLeftHeadingImage {
          description
          localFile {
            childImageSharp {
              gatsbyImageData(width: 256, layout: CONSTRAINED)
            }
          }
        }
      }
      facts {
        title
        summary
        image {
          description
          gatsbyImageData(width: 360, layout: CONSTRAINED)
        }
      }
      path
      variants {
        absolutePath
        ...VariantCardData
      }
      parentSubject {
        ...SubjectParentBreadcrumbs
      }
    }
  }
`;

export default Message;

export const Head = ({ data: { home, message } }: Props) => {
  if (!home || !message) {
    throw new Error("Could not query data");
  }
  const {
    absolutePath,
    pageTitle,
    metaDescription,
    hero,
    summary,
    dmpAttributes,
    id,
  } = message;

  return (
    <GlobalHead
      {...{
        absolutePath,
        home,
        metaDescription: metaDescription ? metaDescription : summary,
        pageTitle,
        shareImgSrc: getSrc(hero?.gatsbyImageData),
        dmpAttributes,
        pageType: "Message",
        testId: "message",
        id,
      }}
    />
  );
};
